import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

// Default platform branding
const DEFAULT_BRANDING = {
  company_name: 'Nexus CMS',
  logo_url: null,
  favicon_url: null,
  primary_color: '#4F46E5',
  secondary_color: '#7C3AED',
  support_email: 'support@nexuscms.com',
  is_reseller: false,
  reseller_slug: null,
};

// Platform default favicon — applied whenever no tenant/reseller favicon exists,
// so a previously-applied favicon never "sticks" across tenants/sessions.
const PLATFORM_DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ''}/favicon.png`;

// Region/app subdomains of the platform itself (e.g. eu.nexuscms.ai). These are
// NOT resellers and must never be treated as a reseller slug.
const PLATFORM_SUBDOMAINS = ['www', 'app', 'preview', 'api', 'localhost', 'eu', 'au', 'us', 'ap', 'staging', 'dev'];

// True when the host is a platform-owned domain (apex or region/app subdomain),
// i.e. nexuscms.ai or <region>.nexuscms.ai — but NOT a tenant subdomain like
// school.eu.nexuscms.ai (4+ labels).
const isPlatformHost = (hostname) => {
  const h = (hostname || '').toLowerCase();
  if (!h.includes('nexuscms')) return false;
  const parts = h.split('.');
  if (parts.length <= 2) return true; // apex: nexuscms.ai
  if (parts.length === 3) return PLATFORM_SUBDOMAINS.includes(parts[0]); // eu.nexuscms.ai
  return false;
};

// Apply a favicon URL to all relevant <link> tags in <head>. Handles plain
// "icon", "shortcut icon", and "apple-touch-icon" so the customer's icon
// wins in every browser + iOS home-screen. Reuses existing tags or creates
// new ones as needed.
const applyFaviconToHead = (faviconUrl) => {
  if (typeof document === 'undefined') return;
  const rels = ['icon', 'shortcut icon', 'apple-touch-icon'];
  rels.forEach((rel) => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  });
};

// Apply the tenant's primary brand colour as the mobile browser chrome tint
// (address-bar background on Safari iOS / Chrome Android). Adds or updates
// the <meta name="theme-color"> tag.
const applyThemeColorToHead = (color) => {
  if (typeof document === 'undefined' || !color) return;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  isReseller: false,
  refreshBranding: () => {},
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const detectReseller = () => {
    const hostname = window.location.hostname;

    // On a platform-owned host (apex or region subdomain like eu.nexuscms.ai),
    // clear any stale reseller slug so a previously-visited reseller's branding
    // (logo/favicon) never bleeds onto the platform marketing/app domain.
    if (isPlatformHost(hostname)) {
      localStorage.removeItem('reseller_slug');
      // Still honor an explicit ?reseller= override for testing.
      const forced = new URLSearchParams(window.location.search).get('reseller');
      return forced || null;
    }

    // Priority 1: URL parameter (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const resellerParam = urlParams.get('reseller');
    if (resellerParam) {
      // Store in localStorage for session persistence
      localStorage.setItem('reseller_slug', resellerParam);
      return resellerParam;
    }

    // Priority 2: Check localStorage (persisted from previous detection)
    const storedReseller = localStorage.getItem('reseller_slug');
    if (storedReseller) {
      return storedReseller;
    }

    // Priority 3: Subdomain detection on the PLATFORM domain only
    // (e.g. agency-abc.nexuscms.ai). Arbitrary custom domains like
    // cms.theagency.com must NOT be parsed as a slug — they are resolved by
    // the domain-based branding lookup in fetchBranding().
    const parts = hostname.split('.');
    if (hostname.includes('nexuscms') && parts.length >= 3) {
      const subdomain = parts[0];
      if (!PLATFORM_SUBDOMAINS.includes(subdomain) && !subdomain.includes('contentfusion')) {
        return subdomain;
      }
    }

    return null;
  };

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const resellerSlug = detectReseller();
      
      if (resellerSlug) {
        // Fetch reseller branding
        const response = await api.get(`/branding?reseller=${resellerSlug}`);
        if (response.data && response.data.is_reseller) {
          setBranding({
            ...DEFAULT_BRANDING,
            ...response.data,
          });

          // Apply reseller favicon, or reset to platform default if they have none.
          updateFavicon(response.data.favicon_url || PLATFORM_DEFAULT_FAVICON);

          // Update document title
          if (response.data.company_name) {
            document.title = response.data.company_name;
          }
        } else {
          // Reseller not found, use default
          clearResellerSession();
          setBranding(DEFAULT_BRANDING);
          updateFavicon(PLATFORM_DEFAULT_FAVICON);
        }
      } else {
        // No reseller detected via slug, check if current hostname is a reseller's custom domain
        const hostname = window.location.hostname;
        const isCustomDomain = !hostname.includes('nexuscms') && 
                               !hostname.includes('localhost') && 
                               !hostname.includes('preview.emergentagent') &&
                               !hostname.includes('127.0.0.1');
        
        if (isCustomDomain) {
          try {
            const domainRes = await api.get(`/branding?domain=${hostname}`);
            if (domainRes.data && domainRes.data.is_reseller && domainRes.data.company_name) {
              setBranding({ ...DEFAULT_BRANDING, ...domainRes.data });
              updateFavicon(domainRes.data.favicon_url || PLATFORM_DEFAULT_FAVICON);
              if (domainRes.data.company_name) document.title = domainRes.data.company_name;
              return;
            }
          } catch (err) {
            // Not a reseller domain, continue to default
          }
        }
        
        // No reseller detected, use default platform branding — UNLESS the
        // logged-in user belongs to a reseller (reseller admin or a customer
        // under a reseller). In that case, apply the reseller's white-label
        // branding even on a platform domain (e.g. au.nexuscms.ai).
        const applied = await applyUserResellerBranding();
        if (!applied) {
          setBranding(DEFAULT_BRANDING);
          updateFavicon(PLATFORM_DEFAULT_FAVICON);
        }
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // On error, clear reseller and use default
      clearResellerSession();
      setBranding(DEFAULT_BRANDING);
      updateFavicon(PLATFORM_DEFAULT_FAVICON);
    } finally {
      setLoading(false);
    }
  };

  // Resolve branding from the authenticated user's reseller (via /branding/me).
  // Returns true when reseller branding was applied.
  const applyUserResellerBranding = async () => {
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || null;
    if (!token) return false;
    try {
      const res = await api.get('/branding/me');
      if (res?.data?.is_reseller) {
        setBranding({ ...DEFAULT_BRANDING, ...res.data });
        // Apply the reseller favicon; the tenant's own favicon (if any) is
        // applied afterward by fetchTenantFavicon and takes precedence.
        updateFavicon(res.data.favicon_url || PLATFORM_DEFAULT_FAVICON);
        if (res.data.company_name) document.title = res.data.company_name;
        return true;
      }
    } catch (e) {
      /* non-fatal */
    }
    return false;
  };

  const clearResellerSession = () => {
    localStorage.removeItem('reseller_slug');
  };

  const updateFavicon = (faviconUrl) => {
    applyFaviconToHead(faviconUrl);
  };

  // Apply the logged-in tenant's favicon (uploaded via Theme Settings) to the
  // admin UI so it shows on every page, not just the Theme Settings page.
  // Called on mount when authenticated, and re-callable via `refreshBranding`
  // after Theme Settings saves a new favicon.
  const fetchTenantFavicon = async () => {
    // Only fetch when a user is authenticated — `/tenant/branding` requires a token.
    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || null;
    if (!token) return;
    try {
      const res = await api.get('/tenant/branding');
      if (res?.data?.favicon_url) {
        applyFaviconToHead(res.data.favicon_url);
      }
    } catch (e) {
      /* non-fatal */
    }
    // Also pull the tenant's brand colour and apply it as the mobile-browser
    // chrome tint via <meta name="theme-color">.
    try {
      const themeRes = await api.get('/theme');
      const color = themeRes?.data?.primary_color;
      if (color) applyThemeColorToHead(color);
    } catch (e) {
      /* non-fatal */
    }
  };

  const refreshBranding = () => {
    fetchBranding();
    fetchTenantFavicon();
  };

  // Clear reseller session (for logout or switching)
  const clearReseller = () => {
    clearResellerSession();
    setBranding(DEFAULT_BRANDING);
    document.title = 'Nexus CMS';
  };

  useEffect(() => {
    // Order matters: fetchBranding sets the reseller/platform-default favicon
    // first, then fetchTenantFavicon overrides ONLY when the logged-in tenant
    // has its own favicon. A new tenant with no favicon therefore shows the
    // platform default — never a stale favicon from a previous tenant/session.
    (async () => {
      await fetchBranding();
      await fetchTenantFavicon();
    })();
  }, []);

  return (
    <BrandingContext.Provider 
      value={{ 
        branding, 
        loading, 
        isReseller: branding.is_reseller,
        refreshBranding,
        clearReseller,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
